// useBrandsFromPantry — queries pantry_items for the user's brands
// on a given canonical, then surfaces them into the brand picker.
//
// Why this exists:
//   The standard observation tier (useBrandObservations / Tier 4)
//   reads from manual_corrections. That table only gets written
//   when admins actually correct a UPC during scan. Regular
//   users adding items to their pantry write to pantry_items but
//   NOT to manual_corrections. Result: a user scans a Ski Queen
//   gjetost UPC, commits it to pantry, then opens the brand
//   picker for gjetost — Ski Queen is invisible because the
//   observation tier doesn't query pantry_items.
//
//   This hook closes that gap. Every brand the user has ever
//   added to pantry for the canonical surfaces in the picker
//   on the next open. Their own scan/add history becomes
//   self-evident.
//
// Returns observations in the same shape useBrandObservations
// returns, so BrandStep can merge them into a single observations
// array passed to brandsForCanonical.

import { useEffect, useState } from "react";
import { supabase } from "./supabase";

export function useBrandsFromPantry(canonicalId) {
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!canonicalId) {
      setBrands([]);
      return;
    }
    let mounted = true;
    (async () => {
      // Get the current user (RLS scopes pantry_items to their family).
      const { data: { user } = {} } = await supabase.auth.getUser();
      if (!user || !mounted) return;

      const { data, error } = await supabase
        .from("pantry_items")
        .select("brand")
        .eq("canonical_id", canonicalId)
        .not("brand", "is", null);
      if (!mounted) return;
      if (error) {
        console.warn("[useBrandsFromPantry] read failed:", error.message);
        return;
      }

      // Aggregate distinct brands by lowercase, preserve display casing.
      const map = new Map();
      for (const row of data || []) {
        const display = (row.brand || "").trim();
        if (!display) continue;
        const key = display.toLowerCase();
        if (!map.has(key)) {
          map.set(key, { display, count: 0 });
        }
        map.get(key).count += 1;
      }
      // Shape matches useBrandObservations: { brand, correction_count }
      // so BrandStep can concatenate the two arrays.
      setBrands(
        [...map.values()].map((r) => ({
          brand: r.display,
          correction_count: r.count,
        })),
      );
    })();
    return () => { mounted = false; };
  }, [canonicalId]);

  return brands;
}
